/**
 * Email Inbox Page - Landlord
 * One inbox for tenant and vendor email: "Tenants & vendors" (filed threads, filterable by type
 * and vendor) and "Other senders" (unrecognized — link to a tenant or vendor, or discard).
 * Full-width thread list with conversation view and inline reply; Compose reaches tenants
 * and vendors together (BR-114..BR-118).
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDomainEvents } from '../hooks/useDomainEvents';
import {
  listThreads,
  getThreadMessages,
  replyToThread,
  discardThread,
  exportEmailHistory,
  getMyEmailAddress,
  ApiClientError,
  type ThreadSummary,
  type ThreadBucket,
  type EmailMessage,
} from '../lib/api';
import { listVendors, type Vendor } from '../api/vendors.api';
import { PageHeader } from '../components/layout/index';
import { Modal } from '../components/Modal';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { ComposeEmailDialog } from '../components/email/ComposeEmailDialog';
import { LinkThreadDialog, type LinkedCounterparty } from '../components/email/LinkThreadDialog';
import { SegmentedRadioGroup } from '../components/email/SegmentedRadioGroup';
import { ThreadRow } from '../components/email/ThreadRow';
import { ThreadView } from '../components/email/ThreadView';

type InboxView = 'list' | 'thread';
type TypeFilter = 'all' | 'tenants' | 'vendors';

// WAI-ARIA APG tabs pattern (automatic activation): ordered left-to-right to match the
// rendered tab order, used for ArrowLeft/ArrowRight wrapping and Home/End.
const BUCKET_ORDER: ThreadBucket[] = ['matched', 'other'];
const BUCKET_LABELS: Record<ThreadBucket, string> = {
  matched: 'Tenants & vendors',
  other: 'Other senders',
};
const EMAIL_TABPANEL_ID = 'email-bucket-tabpanel';
const EMAIL_TAB_IDS: Record<ThreadBucket, string> = {
  matched: 'email-bucket-tab-matched-id',
  other: 'email-bucket-tab-other-id',
};
const TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All', testId: 'email-type-all' },
  { value: 'tenants', label: 'Tenants', testId: 'email-type-tenants' },
  { value: 'vendors', label: 'Vendors', testId: 'email-type-vendors' },
] as const;

type Banner = { kind: 'linked' | 'discarded'; text: string };

const BANNER_CLASS = 'p-3 mb-4 bg-success/10 border border-success/30 rounded-md text-success text-sm';

const tabClass = (selected: boolean): string =>
  `flex-1 md:flex-none px-2 md:px-4 min-h-[44px] text-sm font-medium border-b-2 -mb-px transition-colors ${
    selected
      ? 'border-primary text-foreground'
      : 'border-transparent text-muted-foreground hover:text-foreground'
  }`;

export function EmailInboxPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const bucket: ThreadBucket = searchParams.get('bucket') === 'other' ? 'other' : 'matched';
  const [view, setView] = useState<InboxView>('list');
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [vendorFilter, setVendorFilter] = useState('');
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [threads, setThreads] = useState<ThreadSummary[]>([]);
  const [selectedThread, setSelectedThread] = useState<ThreadSummary | null>(null);
  const [senderAddress, setSenderAddress] = useState('');
  const [threadMessages, setThreadMessages] = useState<EmailMessage[]>([]);
  const [threadGone, setThreadGone] = useState(false);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  // True once the very first fetch (any bucket) has resolved. Gates the full-page skeleton
  // to the initial mount only — a later bucket switch keeps the tablist/tabpanel mounted (an
  // in-panel skeleton takes over instead) so keyboard focus set on the newly-selected tab
  // (WAI-ARIA APG tabs' roving-tabindex/automatic-activation) isn't destroyed mid-keypress.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [myEmailAddress, setMyEmailAddress] = useState<string | null>(null);
  const [addressCopied, setAddressCopied] = useState(false);
  const [isLinkDialogOpen, setIsLinkDialogOpen] = useState(false);
  const [isDiscardOpen, setIsDiscardOpen] = useState(false);
  const [isDiscarding, setIsDiscarding] = useState(false);
  const [banner, setBanner] = useState<Banner | null>(null);
  const cancelDiscardRef = useRef<HTMLButtonElement>(null);

  const fetchThreads = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const { threads: loadedThreads } = await listThreads(bucket, {
        ...(bucket === 'matched' && typeFilter !== 'all' && {
          counterpartyType: typeFilter === 'tenants' ? ('tenant' as const) : ('vendor' as const),
        }),
        ...(bucket === 'matched' && typeFilter === 'vendors' && vendorFilter && { vendorId: vendorFilter }),
      });
      setThreads(loadedThreads);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load threads');
    } finally {
      setIsLoading(false);
      setHasLoadedOnce(true);
    }
  }, [bucket, typeFilter, vendorFilter]);

  useEffect(() => {
    void fetchThreads();
  }, [fetchThreads]);

  // The Vendor select only exists under the Vendors chip, so load the vendor list on first use.
  useEffect(() => {
    if (typeFilter !== 'vendors' || vendors.length > 0) return;
    listVendors()
      .then(({ vendors: loaded }) => setVendors(loaded))
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load vendors'));
  }, [typeFilter, vendors.length]);

  const handleBucketChange = useCallback((nextBucket: ThreadBucket) => {
    setSearchParams(nextBucket === 'matched' ? {} : { bucket: nextBucket });
    setBanner(null);
  }, [setSearchParams]);

  const handleTypeFilterChange = useCallback((next: TypeFilter) => {
    setTypeFilter(next);
    if (next !== 'vendors') setVendorFilter('');
  }, []);

  // Roving-tabindex focus targets for the bucket tablist (WAI-ARIA APG tabs pattern). Both tab
  // buttons are always mounted (only styling/selection changes), so a ref is always attached by
  // the time a keydown handler runs.
  const tabRefs = useRef<Record<ThreadBucket, HTMLButtonElement | null>>({
    matched: null,
    other: null,
  });

  // Automatic activation: moving focus with the keyboard also changes the selected tab and
  // the URL, exactly like a click on that tab would.
  const moveToTab = useCallback((nextBucket: ThreadBucket) => {
    handleBucketChange(nextBucket);
    tabRefs.current[nextBucket]?.focus();
  }, [handleBucketChange]);

  const handleTabListKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    const currentIndex = BUCKET_ORDER.indexOf(bucket);
    let nextIndex: number;
    switch (e.key) {
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + BUCKET_ORDER.length) % BUCKET_ORDER.length;
        break;
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % BUCKET_ORDER.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = BUCKET_ORDER.length - 1;
        break;
      default:
        return;
    }
    e.preventDefault();
    moveToTab(BUCKET_ORDER[nextIndex] ?? 'matched');
  }, [bucket, moveToTab]);

  useEffect(() => {
    getMyEmailAddress()
      .then(({ address }) => setMyEmailAddress(address))
      .catch(() => {
        // Non-critical — the header simply omits the address if this fails.
      });
  }, []);

  const handleCopyAddress = useCallback(async () => {
    if (!myEmailAddress) return;
    try {
      await navigator.clipboard.writeText(myEmailAddress);
      setAddressCopied(true);
      setTimeout(() => setAddressCopied(false), 2000);
    } catch {
      // Fallback for environments without clipboard API
    }
  }, [myEmailAddress]);

  // DC-2 — migrated onto the bus's own type string. 'email.thread_changed' covers the
  // "Other senders" journey — a spam-flagged unmatched message suppresses the usual
  // 'email.message_received' notification (BR-95) but must still refresh the bucket lists.
  useDomainEvents({
    'email.message_received': useCallback(() => {
      void fetchThreads();
    }, [fetchThreads]),
    'email.thread_changed': useCallback(() => {
      void fetchThreads();
    }, [fetchThreads]),
    'email.thread_read': useCallback(() => {
      void fetchThreads();
    }, [fetchThreads]),
  });

  const handleThreadSelect = useCallback(async (threadId: string) => {
    const summary = threads.find((t) => t.threadId === threadId) ?? null;
    try {
      setIsLoadingThread(true);
      setBanner(null);
      setThreadGone(false);
      const { messages } = await getThreadMessages(threadId);
      const firstInbound = messages.find((m) => m.direction === 'INBOUND');
      setSelectedThread(summary);
      setSenderAddress(firstInbound?.fromAddress ?? summary?.participants[0] ?? '');
      setThreadMessages(messages);
      setView('thread');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load thread');
    } finally {
      setIsLoadingThread(false);
    }
  }, [threads]);

  const closeThread = useCallback(() => {
    setView('list');
    setSelectedThread(null);
    setThreadMessages([]);
    setThreadGone(false);
  }, []);

  const handleBackToList = useCallback(() => {
    closeThread();
    // Re-fetch to update read state
    void fetchThreads();
  }, [closeThread, fetchThreads]);

  const handleThreadLinked = useCallback(({ kind, name }: LinkedCounterparty) => {
    setIsLinkDialogOpen(false);
    closeThread();
    setBanner({
      kind: 'linked',
      text: `Filed under ${name} (${kind}). Future emails from ${senderAddress} will match automatically.`,
    });
    void fetchThreads();
  }, [closeThread, fetchThreads, senderAddress]);

  // A 409 means the thread is no longer unmatched — unless it is an address conflict, so check
  // whether the thread is still in Other senders before declaring it gone.
  const handleLinkConflict = useCallback(() => {
    if (!selectedThread) return;
    listThreads('other')
      .then(({ threads: remaining }) => {
        if (!remaining.some((t) => t.threadId === selectedThread.threadId)) {
          setIsLinkDialogOpen(false);
          setThreadGone(true);
        }
      })
      .catch(() => undefined);
  }, [selectedThread]);

  const handleConfirmDiscard = useCallback(async () => {
    if (!selectedThread) return;
    try {
      setIsDiscarding(true);
      await discardThread(selectedThread.threadId);
      setIsDiscardOpen(false);
      closeThread();
      setBanner({ kind: 'discarded', text: 'Thread discarded.' });
      void fetchThreads();
    } catch (err) {
      setIsDiscardOpen(false);
      if (err instanceof ApiClientError && err.status === 409) {
        setThreadGone(true);
      } else {
        setError(err instanceof Error ? err.message : 'Failed to discard thread');
      }
    } finally {
      setIsDiscarding(false);
    }
  }, [selectedThread, closeThread, fetchThreads]);

  const handleSendReply = useCallback(async (bodyText: string) => {
    if (!selectedThread) return;
    try {
      setError(null);
      await replyToThread(selectedThread.threadId, bodyText);
      const { messages } = await getThreadMessages(selectedThread.threadId);
      setThreadMessages(messages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reply');
      throw err;
    }
  }, [selectedThread]);

  const handleExport = useCallback(async () => {
    try {
      const response = await exportEmailHistory({});
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const contentDisposition = response.headers.get('Content-Disposition');
      const filename = contentDisposition
        ? contentDisposition.split('filename="')[1]?.replace('"', '') || 'email-export.zip'
        : 'email-export.zip';

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export emails');
    }
  }, []);

  // The list can refresh while a thread is open; prefer the fresh summary, keep the snapshot otherwise.
  const activeThread = selectedThread
    ? (threads.find((t) => t.threadId === selectedThread.threadId) ?? selectedThread)
    : undefined;

  if (isLoading && !hasLoadedOnce) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <div className="mb-4 flex items-center justify-between gap-3">
          <Skeleton className="h-7 w-40" />
          <div className="flex gap-3">
            <Skeleton className="h-10 w-20" />
            <Skeleton className="h-10 w-24" />
          </div>
        </div>
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div
      className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]"
      data-testid="email-inbox-page"
    >
      <PageHeader
        title="Email"
        subtitleWrap
        subtitleNode={
          myEmailAddress && (
            <span className="flex flex-wrap items-center gap-x-2 gap-y-0 min-w-0">
              <span>Your address:</span>
              <code
                data-testid="my-email-address"
                className="px-1.5 py-0.5 bg-accent/50 rounded text-foreground font-mono text-xs min-w-0 break-all"
              >
                {myEmailAddress}
              </code>
              <button
                type="button"
                onClick={handleCopyAddress}
                data-testid="copy-my-email-address"
                aria-label={`Copy email address: ${myEmailAddress}`}
                className="min-h-[44px] px-2 text-xs text-primary hover:underline shrink-0"
              >
                {addressCopied ? 'Copied!' : 'Copy'}
              </button>
            </span>
          )
        }
        action={
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={handleExport}
              data-testid="export-button"
            >
              Export
            </Button>
            <Button
              onClick={() => setIsComposeOpen(true)}
              data-testid="compose-button"
            >
              Compose
            </Button>
          </div>
        }
      />

      {error && (
        <div
          role="alert"
          className="p-3 mb-4 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm flex items-center justify-between gap-3"
          data-testid="error-message"
        >
          <span>{error}</span>
          <Button
            variant="outline"
            onClick={() => void fetchThreads()}
            data-testid="retry-button"
          >
            Retry
          </Button>
        </div>
      )}

      {/* Thread List View */}
      {view === 'list' && (
        <>
          {banner?.kind === 'linked' && (
            <div role="status" data-testid="link-thread-success" className={BANNER_CLASS}>
              {banner.text}
            </div>
          )}
          {banner?.kind === 'discarded' && (
            <div role="status" data-testid="discard-thread-success" className={BANNER_CLASS}>
              {banner.text}
            </div>
          )}

          <div
            role="tablist"
            aria-label="Email bucket"
            className="flex gap-1 mb-4 border-b border-border"
            onKeyDown={handleTabListKeyDown}
          >
            <button
              ref={(el) => { tabRefs.current.matched = el; }}
              type="button"
              role="tab"
              id={EMAIL_TAB_IDS.matched}
              aria-selected={bucket === 'matched'}
              aria-controls={EMAIL_TABPANEL_ID}
              tabIndex={bucket === 'matched' ? 0 : -1}
              onClick={() => handleBucketChange('matched')}
              data-testid="email-bucket-tab-matched"
              className={tabClass(bucket === 'matched')}
            >
              {BUCKET_LABELS.matched}
            </button>
            <button
              ref={(el) => { tabRefs.current.other = el; }}
              type="button"
              role="tab"
              id={EMAIL_TAB_IDS.other}
              aria-selected={bucket === 'other'}
              aria-controls={EMAIL_TABPANEL_ID}
              tabIndex={bucket === 'other' ? 0 : -1}
              onClick={() => handleBucketChange('other')}
              data-testid="email-bucket-tab-other"
              className={tabClass(bucket === 'other')}
            >
              {BUCKET_LABELS.other}
            </button>
          </div>

          {bucket === 'matched' && (
            <div className="mb-4 space-y-3">
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm font-semibold text-foreground">Show</span>
                <SegmentedRadioGroup
                  ariaLabel="Show"
                  options={[...TYPE_FILTER_OPTIONS]}
                  value={typeFilter}
                  onChange={handleTypeFilterChange}
                  variant="chips"
                  testId="email-type-filter"
                />
              </div>
              {typeFilter === 'vendors' && (
                <div>
                  <label htmlFor="email-vendor-filter" className="block text-sm font-medium text-foreground mb-1.5">
                    Vendor
                  </label>
                  <select
                    id="email-vendor-filter"
                    value={vendorFilter}
                    onChange={(e) => setVendorFilter(e.target.value)}
                    data-testid="email-vendor-filter"
                    className="w-full md:w-72 px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground min-h-[44px]"
                  >
                    <option value="">All vendors</option>
                    {vendors.map((vendor) => (
                      <option key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          <div
            role="tabpanel"
            id={EMAIL_TABPANEL_ID}
            aria-labelledby={EMAIL_TAB_IDS[bucket]}
            tabIndex={0}
          >
          {isLoadingThread && (
            <div className="py-2 mb-2 text-sm text-muted-foreground">Loading thread...</div>
          )}
          {isLoading ? (
            <div className="space-y-2" data-testid="thread-list-loading">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : threads.length === 0 ? (
            bucket === 'other' ? (
              <div
                className="py-8 text-center text-muted-foreground"
                data-testid="other-senders-empty"
              >
                <p>Emails sent to your address from people who aren&apos;t one of your tenants or vendors will appear here.</p>
              </div>
            ) : (
              <div
                className="py-8 text-center text-muted-foreground"
                data-testid="empty-threads"
              >
                <p>{typeFilter === 'all' ? 'No email threads yet' : 'No email threads match this filter'}</p>
              </div>
            )
          ) : (
            <div
              className="border border-border rounded-md overflow-hidden"
              data-testid="thread-list"
            >
              {threads.map((thread, index) => (
                <ThreadRow
                  key={thread.threadId}
                  thread={thread}
                  isLast={index === threads.length - 1}
                  showSpam={bucket === 'other'}
                  onSelect={(id) => void handleThreadSelect(id)}
                />
              ))}
            </div>
          )}
          </div>
        </>
      )}

      {/* Thread View */}
      {view === 'thread' && (
        <ThreadView
          thread={activeThread}
          messages={threadMessages}
          senderAddress={senderAddress}
          gone={threadGone}
          onBack={handleBackToList}
          onLink={() => setIsLinkDialogOpen(true)}
          onDiscard={() => setIsDiscardOpen(true)}
          onSendReply={handleSendReply}
          onError={setError}
        />
      )}

      {isComposeOpen && (
        <ComposeEmailDialog
          onClose={() => setIsComposeOpen(false)}
          onEmailSent={() => {
            void fetchThreads();
            setIsComposeOpen(false);
          }}
        />
      )}

      {isLinkDialogOpen && activeThread && (
        <LinkThreadDialog
          isOpen={isLinkDialogOpen}
          threadId={activeThread.threadId}
          senderAddress={senderAddress}
          onClose={() => setIsLinkDialogOpen(false)}
          onLinked={handleThreadLinked}
          onConflict={handleLinkConflict}
        />
      )}

      <Modal
        isOpen={isDiscardOpen}
        onClose={() => setIsDiscardOpen(false)}
        title="Discard this thread?"
        role="alertdialog"
        initialFocusRef={cancelDiscardRef}
        hideCloseButton
        data-testid="discard-thread-dialog"
      >
        <p className="text-sm text-muted-foreground m-0">
          The thread from <span className="font-semibold text-foreground">{senderAddress}</span> will leave
          Other senders. If they write again, it will show up here again.
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <Button
            ref={cancelDiscardRef}
            type="button"
            variant="outline"
            onClick={() => setIsDiscardOpen(false)}
            disabled={isDiscarding}
            data-testid="discard-thread-cancel"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={() => void handleConfirmDiscard()}
            disabled={isDiscarding}
            data-testid="discard-thread-confirm"
          >
            {isDiscarding ? 'Discarding...' : 'Discard thread'}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
